// =============================================
// 3. FIXED ROLE REPORT WITH FUNCTION DECLARATION BUG
// components/reports/RoleReport.js
// =============================================

import React, { useMemo } from 'react'
import { UserCheck, Clock, DollarSign, TrendingUp, Award } from 'lucide-react'
import { processTimeEntries } from '../../utils/timeSplitUtils'

// Move formatRoleName outside of component to fix initialization error
const formatRoleName = (role) => {
  if (!role || role === 'unassigned') return 'Unassigned'
  
  const roleLabels = {
    'senior_developer': 'Senior Developer',
    'project_manager': 'Project Manager', 
    'developer': 'Developer',
    'designer': 'Designer',
    'qa_engineer': 'QA Engineer',
    'tech_lead': 'Tech Lead',
    'consultant': 'Consultant',
    'architect': 'Architect',
    'junior_developer': 'Junior Developer',
    'devops_engineer': 'DevOps Engineer',
    'business_analyst': 'Business Analyst',
    'product_manager': 'Product Manager'
  }
  
  return roleLabels[role] || role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

const getRoleColor = (roleName) => {
  const colors = {
    'senior_developer': '#10B981',
    'project_manager': '#3B82F6', 
    'developer': '#8B5CF6',
    'designer': '#F59E0B',
    'qa_engineer': '#EF4444',
    'tech_lead': '#06B6D4',
    'consultant': '#84CC16',
    'architect': '#6366F1',
    'junior_developer': '#EC4899',
    'devops_engineer': '#F97316',
    'business_analyst': '#14B8A6',
    'product_manager': '#8B5A2B',
    'unassigned': '#6B7280'
  }
  return colors[roleName] || '#6B7280'
}

export const RoleReport = ({ data }) => {
  const roleData = useMemo(() => {
    if (!data.length) return []

    // Process time entries to handle splits from the separate table
    const expandedEntries = processTimeEntries(data)
    const roleMap = new Map()
    
    // Keep track of original entries per role
    const roleOriginalCounts = new Map()
    data.forEach(entry => {
      const billRateRole = entry.bill_rate_role || 'unassigned'
      roleOriginalCounts.set(billRateRole, (roleOriginalCounts.get(billRateRole) || 0) + 1)
    })
    
    expandedEntries.forEach(entry => {
      // Use bill_rate_role from tenant_memberships for billing analysis
      const billRateRole = entry.bill_rate_role || 'unassigned'
      
      if (!roleMap.has(billRateRole)) {
        roleMap.set(billRateRole, {
          id: billRateRole,
          name: formatRoleName(billRateRole),
          type: 'bill_rate_role',
          totalHours: 0,
          billableHours: 0,
          revenue: 0,
          expandedEntries: 0,
          splitCount: 0,
          users: new Set(),
          projects: new Set(),
          clients: new Set()
        })
      }
      
      const role = roleMap.get(billRateRole)
      role.totalHours += entry.duration_hours || 0
      role.expandedEntries += 1
      
      if (entry.is_split_entry) {
        role.splitCount += 1
      }
      
      role.users.add(entry.user_id)
      role.projects.add(entry.project_id)
      role.clients.add(entry.client_id)
      
      if (entry.is_billable) {
        role.billableHours += entry.duration_hours || 0
        role.revenue += (entry.duration_hours || 0) * (entry.hourly_rate || 0)
      }
    })
    
    return Array.from(roleMap.values()).map(role => ({
      ...role,
      userCount: role.users.size,
      projectCount: role.projects.size,
      clientCount: role.clients.size,
      utilizationRate: role.totalHours > 0 ? (role.billableHours / role.totalHours) * 100 : 0,
      avgRate: role.billableHours > 0 ? role.revenue / role.billableHours : 0,
      originalEntries: roleOriginalCounts.get(role.id) || 0
    })).sort((a, b) => b.revenue - a.revenue)
  }, [data])

  if (!roleData.length) {
    return (
      <div className="text-center py-12">
        <UserCheck className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <div className="text-slate-600">No role data found for the selected filters</div>
      </div>
    )
  }

  const topRole = roleData[0]
  const totalRevenue = roleData.reduce((sum, role) => sum + role.revenue, 0)
  const totalSplits = roleData.reduce((sum, role) => sum + role.splitCount, 0)

  return (
    <div className="space-y-6">
      {/* Top Performing Role */}
      {topRole && (
        <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <Award className="w-5 h-5" />
                <span className="text-sm font-medium opacity-90">Top Performing Role</span>
              </div>
              <h3 className="text-2xl font-bold mb-1">{topRole.name}</h3>
              <div className="flex items-center space-x-4 text-sm opacity-90">
                <span>${topRole.revenue.toFixed(0)} cost</span>
                <span>{topRole.totalHours.toFixed(1)}h total</span>
                <span>{topRole.userCount} team members</span>
                {topRole.splitCount > 0 && <span>{topRole.splitCount} splits</span>}
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold">${topRole.avgRate.toFixed(0)}</div>
              <div className="text-sm opacity-90">avg rate/hr</div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      {totalSplits > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="text-sm text-blue-800">
            <strong>{totalSplits} split entries</strong> have been processed and attributed to their respective billing roles
          </div>
        </div>
      )}

      {/* Role Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {roleData.map((role, index) => (
          <div 
            key={role.id} 
            className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-shadow"
            style={{ borderLeftColor: getRoleColor(role.id), borderLeftWidth: '4px' }}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-semibold text-slate-900">{role.name}</h3>
                <p className="text-sm text-slate-600">
                  {role.userCount} members • {role.projectCount} projects
                </p>
                {role.splitCount > 0 && (
                  <p className="text-xs text-blue-600 mt-1">
                    {role.splitCount} split entries processed
                  </p>
                )}
              </div>
              {index === 0 && (
                <div className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full font-medium">
                  Top Role
                </div>
              )}
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <div className="flex items-center text-sm text-slate-600">
                  <Clock className="w-4 h-4 mr-1" />
                  <span>Hours</span>
                </div>
                <div className="text-right">
                  <span className="font-medium text-slate-900">{role.totalHours.toFixed(1)}h</span>
                  <div className="text-xs text-slate-500">
                    {role.originalEntries} entries{role.splitCount > 0 ? ` (+${role.splitCount} splits)` : ''}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-between items-center">
                <div className="flex items-center text-sm text-slate-600">
                  <DollarSign className="w-4 h-4 mr-1" />
                  <span>Cost</span>
                </div>
                <div className="text-right">
                  <span className="font-medium text-green-600">${role.revenue.toFixed(0)}</span>
                  <div className="text-xs text-slate-500">
                    {totalRevenue > 0 ? ((role.revenue / totalRevenue) * 100).toFixed(1) : 0}% of total
                  </div>
                </div>
              </div>
              
              <div className="flex justify-between items-center">
                <div className="flex items-center text-sm text-slate-600">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  <span>Avg Rate</span>
                </div>
                <span className="font-medium text-slate-900">${role.avgRate.toFixed(0)}/h</span>
              </div>
              
              {/* Utilization Bar */}
              <div className="pt-2">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-slate-600">Utilization</span>
                  <span className="text-xs font-medium text-slate-900">{role.utilizationRate.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div 
                    className="h-2 rounded-full transition-all duration-300"
                    style={{ 
                      backgroundColor: getRoleColor(role.id),
                      width: `${Math.min(role.utilizationRate, 100)}%` 
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Detailed Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h3 className="text-lg font-semibold text-slate-900">Role Performance Analysis</h3>
          <p className="text-sm text-slate-600 mt-1">Cost and productivity breakdown by billing role (includes splits from time_entry_splits table)</p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Team Members
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Projects
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Total Hours
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Cost
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Avg Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Utilization
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {roleData.map((role, index) => (
                <tr key={role.id} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded-full mr-3"
                        style={{ backgroundColor: getRoleColor(role.id) }}
                      />
                      <div>
                        <div className="font-medium text-slate-900">{role.name}</div>
                        <div className="text-sm text-slate-500">
                          {role.originalEntries} entries{role.splitCount > 0 ? ` (+${role.splitCount} splits)` : ''}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">{role.userCount}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">{role.projectCount}</td>
                  <td className="px-6 py-4 text-sm font-medium text-slate-900">
                    {role.totalHours.toFixed(1)}h
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-green-600">
                    ${role.revenue.toFixed(0)}
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-slate-900">
                    ${role.avgRate.toFixed(0)}/h
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="w-16 bg-slate-200 rounded-full h-2 mr-2">
                        <div 
                          className="h-2 rounded-full transition-all duration-300"
                          style={{ 
                            backgroundColor: getRoleColor(role.id),
                            width: `${Math.min(role.utilizationRate, 100)}%` 
                          }}
                        />
                      </div>
                      <span className="text-sm font-medium text-slate-900">
                        {role.utilizationRate.toFixed(1)}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}


export default RoleReport